import { saneOption, Scope } from 'logard';
import ChatGroup, { ChatGroupId } from '@/chat/models/ChatGroup';
import ChatService from '@/chat/services/ChatService';
import Identity from '@/general/models/Identity';
import WorkshopTeam, { WorkshopTeamId } from '@/general/models/WorkshopTeam';
import AuthService from '@/general/services/AuthService';
import ReferenceService from '@/general/services/ReferenceService';
import CommonFilterType, { OUTLET_FILTER_TYPES } from '../constants/CommonFilterType';
import TagType from '../constants/TagType';
import { parseNumericId } from '../converters/commonConverters';
import Brand, { BrandId } from '../models/Brand';
import CommonFilters from '../models/CommonFilters';
import { OutletId } from '../models/Outlet';
import Tag, { TagId } from '../models/Tag';
import User, { UserId } from '../models/User';
import { extractIds } from '../utils';
import { saneNumericId } from '../utils/sanitizers';
import { isPresent, present } from '../utils/typescript';
import {
  BRAND_QUERY_PARAM,
  BRANDS_STORAGE_KEY,
  DOSSIER_TAG_QUERY_PARAM,
  DOSSIER_TAGS_STORAGE_KEY,
  GROUP_QUERY_PARAM,
  GROUPS_STORAGE_KEY,
  OUTLET_QUERY_PARAM,
  OUTLETS_STORAGE_KEY,
  TEAM_QUERY_PARAM,
  TEAMS_STORAGE_KEY,
  USER_QUERY_PARAM,
  USERS_STORAGE_KEY,
} from '@/shared/utils/filters';

class FilterService {
  async getFilters(scope: Scope, activeTypes: Array<CommonFilterType>): Promise<CommonFilters> {
    const user = AuthService.getCurrentUser();
    const activeTypeSet = new Set(activeTypes);

    const outletIds = this.getOutletIds(scope, activeTypeSet, user);

    const brands = user.getAvailableBrands(outletIds);
    const brandId = this.getBrandId(scope, brands, activeTypeSet);

    const teams = user.getAvailableTeams(outletIds);
    const teamId = this.getTeamId(scope, teams, activeTypeSet);

    const users = ReferenceService.getActiveUsers();
    const userIds = this.getUserIds(scope, users, activeTypeSet);

    const groups = await this.loadGroups(outletIds, activeTypeSet);
    const groupIds = this.getGroupIds(scope, groups, activeTypeSet);

    const tags = await this.loadDossierTags(outletIds, activeTypeSet);
    const tagIds = this.getDossierTagIds(scope, tags, activeTypeSet);

    return new CommonFilters(user.outlets, outletIds, brands, brandId, teams, teamId, users, userIds, groups, groupIds, tags, tagIds, activeTypeSet);
  }

  private getOutletIds(scope: Scope, activeTypes: Set<CommonFilterType>, user: Identity): Array<OutletId> {
    if (OUTLET_FILTER_TYPES.filter(filter => activeTypes.has(filter)).length > 1) {
      throw new Error('multiple outlet filters active at the same time!');
    }

    if (user.outlets.length === 0) {
      OUTLET_FILTER_TYPES.forEach(filterType => activeTypes.delete(filterType));
    }

    return this.parseOutletIds(scope, activeTypes, user);
  }

  private parseOutletIds(scope: Scope, activeTypes: Set<CommonFilterType>, user: Identity): Array<OutletId> {
    const oldValue = sessionStorage.getItem(OUTLETS_STORAGE_KEY)
      ?.split(',')
      .map(outletId => parseNumericId<OutletId>(outletId))
      .filter(isPresent) ?? [];

    const options = user.outlets.map(outlet => outlet.id);
    if (activeTypes.has(CommonFilterType.OUTLET_OPTIONAL)) {
      const fallback = user.outlets.length > 1 ? oldValue[oldValue.length - 1] : user.outlets[0].id;
      const id = scope.getQueryParam(OUTLET_QUERY_PARAM, saneOption(options, saneNumericId<OutletId>), fallback);
      return [id].filter(isPresent);
    } else if (activeTypes.has(CommonFilterType.OUTLET_MANDATORY)) {
      const fallback = present(oldValue[0] ?? user.mainOutletId ?? user.outlets[0].id);
      const id = scope.getQueryParam(OUTLET_QUERY_PARAM, saneOption(options, saneNumericId<OutletId>), fallback);
      return [present(id)];
    } else if (activeTypes.has(CommonFilterType.OUTLET_MULTIPLE)) {
      const fallback = user.outlets.length > 1 ? oldValue : options;
      return scope.getQueryParams(OUTLET_QUERY_PARAM, saneOption(options, saneNumericId<OutletId>), fallback);
    } else {
      scope.removeQueryParams(OUTLET_QUERY_PARAM);
      return [];
    }
  }

  private getBrandId(scope: Scope, brands: Array<Brand>, activeTypes: Set<CommonFilterType>): BrandId | null {
    if (brands.length === 0) activeTypes.delete(CommonFilterType.BRAND_OPTIONAL);
    if (activeTypes.has(CommonFilterType.BRAND_OPTIONAL)) {
      const sessionValue = sessionStorage.getItem(BRANDS_STORAGE_KEY)?.split(',') ?? [];
      const storedBrandId = parseNumericId<BrandId>(sessionValue[sessionValue.length - 1]) ?? undefined;
      const options = brands.map(b => b.id);
      const fallback = isPresent(storedBrandId) && options.includes(storedBrandId) ? storedBrandId : undefined;
      return scope.getQueryParam(BRAND_QUERY_PARAM, saneOption(options, saneNumericId<BrandId>), fallback) ?? null;
    } else {
      scope.removeQueryParams(BRAND_QUERY_PARAM);
      return null;
    }
  }

  private getTeamId(scope: Scope, teams: Array<WorkshopTeam>, activeTypes: Set<CommonFilterType>): WorkshopTeamId | null {
    if (teams.length === 0) activeTypes.delete(CommonFilterType.TEAM_OPTIONAL);
    if (activeTypes.has(CommonFilterType.TEAM_OPTIONAL)) {
      const sessionValue = sessionStorage.getItem(TEAMS_STORAGE_KEY)?.split(',') ?? [];
      const storedTeamId = parseNumericId<WorkshopTeamId>(sessionValue[sessionValue.length - 1]) ?? undefined;
      const options = teams.map(t => t.id);
      const fallback = isPresent(storedTeamId) && options.includes(storedTeamId) ? storedTeamId : undefined;
      return scope.getQueryParam(TEAM_QUERY_PARAM, saneOption(options, saneNumericId<WorkshopTeamId>), fallback) ?? null;
    } else {
      scope.removeQueryParams(TEAM_QUERY_PARAM);
      return null;
    }
  }

  private getUserIds(scope: Scope, users: Array<User>, activeTypes: Set<CommonFilterType>): Array<UserId> {
    if (activeTypes.has(CommonFilterType.USER_MULTIPLE)) {
      const options = extractIds(users);
      const storedUserIds = sessionStorage.getItem(USERS_STORAGE_KEY)?.split(',').map(id => saneNumericId<UserId>(id)) ?? [];
      const fallback = storedUserIds.filter(suId => options.includes(suId));
      return scope.getQueryParams(USER_QUERY_PARAM, saneOption(options, saneNumericId<UserId>), fallback);
    } else {
      scope.removeQueryParams(USER_QUERY_PARAM);
      return [];
    }
  }

  private async loadGroups(outletIds: Array<OutletId>, activeTypeSet: Set<CommonFilterType>): Promise<Array<ChatGroup>> {
    if (outletIds.length !== 1 || !activeTypeSet.has(CommonFilterType.GROUP_MULTIPLE)) return [];
    const container = await ChatService.loadInternalTargets(outletIds[0]);
    return container.groups;
  }

  private getGroupIds(scope: Scope, groups: Array<ChatGroup>, activeTypes: Set<CommonFilterType>): Array<ChatGroupId> {
    if (groups.length === 0) activeTypes.delete(CommonFilterType.GROUP_MULTIPLE);
    if (activeTypes.has(CommonFilterType.GROUP_MULTIPLE)) {
      const options = extractIds(groups);
      const storedGroupIds = sessionStorage.getItem(GROUPS_STORAGE_KEY)?.split(',') ?? [];
      const fallback = storedGroupIds.filter(sgId => options.includes(sgId));
      return scope.getQueryParams(GROUP_QUERY_PARAM, saneOption(options, saneNumericId<ChatGroupId>), fallback);
    } else {
      scope.removeQueryParams(GROUP_QUERY_PARAM);
      return [];
    }
  }

  private async loadDossierTags(outletIds: Array<OutletId>, activeTypeSet: Set<CommonFilterType>): Promise<Array<Tag>> {
    if (outletIds.length !== 1 || !activeTypeSet.has(CommonFilterType.DOSSIER_TAG)) return [];
    return ReferenceService.loadTags(TagType.DOSSIER, outletIds[0]);
  }

  private getDossierTagIds(scope: Scope, tags: Array<Tag>, activeTypes: Set<CommonFilterType>): Array<TagId> {
    if (tags.length === 0) activeTypes.delete(CommonFilterType.DOSSIER_TAG);
    if (activeTypes.has(CommonFilterType.DOSSIER_TAG)) {
      const options = extractIds(tags);
      const storedTagIds = sessionStorage.getItem(DOSSIER_TAGS_STORAGE_KEY)?.split(',').map(id => saneNumericId<TagId>(id)) ?? [];
      const fallback = storedTagIds.filter(stId => options.includes(stId));
      return scope.getQueryParams(DOSSIER_TAG_QUERY_PARAM, saneOption(options, saneNumericId<TagId>), fallback);
    } else {
      scope.removeQueryParams(DOSSIER_TAG_QUERY_PARAM);
      return [];
    }
  }
}

export default new FilterService();
